import { useEquitiesSymbolSearchService } from '../hooks/useSymbolSearchService';
import type { SymbolPickerState, FocusedElement } from '../state/types';
import { SymbolInput } from './SymbolInput';
import type { SymbolInputRef } from './SymbolInput';
import { forwardRef, useEffect, useState } from 'react';
import { useSecurityMasterEntry } from 'src/features/security-master';
import { useEvent } from 'src/hooks/useEvent';
import type { RecentlyUsedSymbolsService } from 'src/services/RecentlyUsedSymbolsService';

export interface EquitiesProps {
    // State
    state: SymbolPickerState;
    // Event handlers
    onUnderlyingSymbolChange: (symbol: string) => void;
    onUnderlyingSymbolClear: () => void;
    onDropdownSearchForElement: (element: FocusedElement, term: string) => void;
    onDropdownItems: (element: FocusedElement, items: any[]) => void;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
    clearable?: boolean;
    placeholder?: string;
    recentlyUsedSymbolsService: RecentlyUsedSymbolsService;
}

export const Equities = forwardRef<SymbolInputRef, EquitiesProps>(
    (
        {
            state,
            onUnderlyingSymbolChange,
            onUnderlyingSymbolClear,
            onDropdownSearchForElement,
            onDropdownItems,
            onDropdownActiveIndex,
            onFocus,
            clearable,
            placeholder,
            recentlyUsedSymbolsService,
        },
        ref,
    ) => {
        const { symbolSearchService } = useEquitiesSymbolSearchService();

        const [isInvalidSymbol, setIsInvalidSymbol] = useState(false);

        // used when InstrumentType is equity to validate if we have a "real" symbol match
        const { data: equitySymbolMatch, isLoading: equitySymbolMatchLoading } = useSecurityMasterEntry(
            state.instrument.underlyingSymbol,
        );

        useEffect(() => {
            setIsInvalidSymbol(
                Boolean(state.instrument.underlyingSymbol) && !equitySymbolMatch && !equitySymbolMatchLoading,
            );
        }, [equitySymbolMatch, equitySymbolMatchLoading, state.instrument.underlyingSymbol]);

        const handleDropdownSearch = useEvent((term: string) => {
            onDropdownSearchForElement('underlyingSymbol', term);
        });

        return (
            <SymbolInput
                ref={ref}
                dropdownOpen={state.openDropdown === 'underlyingSymbol'}
                value={state.instrument.underlyingSymbol}
                className="min-w-0 flex-1 flex-grow rounded-none"
                recentlyUsedSymbolsService={recentlyUsedSymbolsService}
                symbolSearchService={symbolSearchService}
                onSelect={onUnderlyingSymbolChange}
                onClear={onUnderlyingSymbolClear}
                isInvalidSymbol={isInvalidSymbol}
                dropdown={state.dropdowns.underlyingSymbol}
                onDropdownSearch={handleDropdownSearch}
                onDropdownItems={onDropdownItems}
                onDropdownActiveIndex={onDropdownActiveIndex}
                onFocus={onFocus}
                clearable={clearable}
                placeholder={placeholder}
            />
        );
    },
);

Equities.displayName = 'Equities';
